import React, { useState, useEffect } from "react";
import sanityClient from "../client"; // Sanity client
import { motion } from "framer-motion"; // For animations
import { Link as ScrollLink } from "react-scroll"; // For scrolling
import { useNavigate } from "react-router-dom"; // For navigation
import "./styles/Home.scss"; // Stylesheet

export default function Home() {
  const [homepageImages, setHomepageImages] = useState([]);
  const navigate = useNavigate(); // Initialize navigate hook

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "homepageImages"]{
          _id,
          description,
          "imageUrl": image.asset->url
        }`
      )
      .then((data) => setHomepageImages(data))
      .catch(console.error);
  }, []);

  return (
    <div className="home">
      {/* Welcome Message Section */}
      <section className="header-section">
        <h1>Del Music Foundation</h1>
        <p>Empowering the community through music and education.</p>
        <div className="header-actions">
          <div 
            className="action-item left" 
            onClick={() => navigate("/Donations")} // Navigate to Donations
          >
            <span>Donate</span>
            <i className="down-icon">▼</i>{" "}
          </div>
          <div 
            className="action-item"
            onClick={() => navigate("/success-stories")}
          >
            <span>See Our Impact </span>
            <i className="down-icon">▼</i>{" "}
          </div>
          <ScrollLink 
            to="about-section" 
            smooth={true} 
            duration={500} 
            className="action-item right"
          >
            <span>Learn More</span>
            <i className="down-icon">▼</i>{" "}
          </ScrollLink>
        </div>
      </section>

      {/* About Section */}
      <section id="about-section" className="about-section">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="about-content"
        >
          <div className="main-text">
            <h2>About the Foundation</h2>
            <p>
              The Del Couch Music Education Foundation was formed to provide
              students with opportunities to create and learn about music and to
              provide an understanding of the music business, to record and
              listen to their musical creations, and to provide a platform which
              can enhance their musical, cultural, academic, and life
              opportunities. Music has the power to enrich our communities, in
              that music inspires and cultivates creativity, critical thinking,
              and self-expression. Young people, through music, share their
              feelings and opinions; sometimes music is their only path to do
              so.
            </p>
          </div>
          <div className="quote-section">
            <blockquote>
              “We believe in the love of music and have a passion for sharing
              that love with our students. Those students who come to us share
              the passion, to express themselves, to be heard, to create, and
              sometimes just to sing out. It's a passion they will carry
              throughout their lives, regardless of whether they simply have a
              love of the art, or become highly successful in the business of
              music. They, in their own ways, will share this passion all
              through their lives and will never forget the opportunities
              provided to them by this foundation—to listen to their own music,
              to hear their own voices, to record their own sounds and to pursue
              a dream.”
            </blockquote>
            <span className="quote-author">— Del Couch</span>
          </div>
        </motion.div>
      </section>

      {/* Photos Section */}
      <section className="photos-section">
        <h2>Success Stories</h2>
        {homepageImages.map((item, index) => (
          <motion.div
            key={item._id}
            className={`photo-item ${index % 2 === 0 ? "left" : "right"}`}
            initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
          >
            <img src={item.imageUrl} alt={item.description} />
            <div className="description">{item.description}</div>
          </motion.div>
        ))}
      </section>

      {/* Upcoming Events Section */}
      <UpcomingEvents />
    </div>
  );
}

function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "upcomingEvents"]{
          _id,
          title,
          description,
          date,
          "imageUrl": image.asset->url
        }`
      )
      .then((data) => setEvents(data))
      .catch(console.error);
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? events.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === events.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleImageClick = (imageUrl) => {
    const newWindow = window.open(imageUrl, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  return (
    <section className="upcoming-events">
      <h2>Upcoming Events and Tours</h2>
      {events.length > 0 && (
        <div className="carousel">
          <button className="button left" onClick={handlePrev}>
            ❮
          </button>
          <motion.img
            src={events[currentIndex].imageUrl}
            alt={events[currentIndex].title}
            onClick={() => handleImageClick(events[currentIndex].imageUrl)}
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          />
          <button className="button right" onClick={handleNext}>
            ❯
          </button>
        </div>
      )}
    </section>
  );
}
